import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import { Search, ShoppingCart, Menu as MenuIcon, Gift, Heart, Wallet, Star, LogOut } from 'lucide-react';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context';

const Navbar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { authState, resetAuthState } = useAuth();
  const cart = useCart();

  // Dynamic user information
  const userName = `${authState?.firstName || ''} ${authState?.lastName || ''}`.trim();
  const userInitials = `${authState?.firstName?.[0] || ''}${authState?.lastName?.[0] || ''}`.toUpperCase();
  const userTier = authState?.personaStatus === 'approved' ? 2 : 1;

  // Tier image paths
  const tierImages = {
    1: '/img/profile/tier1.svg',
    2: '/img/profile/tier2.svg'  
  };

  const handleSignOut = () => {
    resetAuthState();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/sign-in');
  };

  const handleSettings = () => {
    navigate('/account-overview');
  };

  const cartItemCount = cart?.cartItems?.length || 0;

  const handleCartNavigation = () => {
    navigate(cartItemCount > 0 ? '/checkout' : '/empty');
  };

  return (
    <nav className="w-full bg-white py-4 px-6 shadow-sm">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/* Logo */}
        <div className="flex-shrink-0">
          <img
            src="/img/header/logo-light.png"
            alt="Lipaworld"
            className="h-8 w-auto" />
        </div>

        {/* Search Bar */}
        {/* <div className="hidden md:flex flex-1 max-w-2xl mx-4">
          <div className="relative w-full">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="What are you looking for?"
              className="w-full px-4 py-2 rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500" />
            <button className="absolute right-2 top-1/2 -translate-y-1/2">
              <Search className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div> */}

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Cart */}
          <button className="relative" onClick={handleCartNavigation}>
            <ShoppingCart className="h-6 w-6" />
            {cartItemCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {cartItemCount}
              </span>
            )}
          </button>

          {/* Menu Dropdown */}
          <Menu as="div" className="relative">
            <Menu.Button className={`flex items-center justify-center p-2 rounded-full border border-black focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-green-500 focus:border-green-500`}>
              <div className="h-8 w-8 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-medium mr-2">
                {userInitials}
              </div>
              <MenuIcon className="h-6 w-6" />
            </Menu.Button>
          
            <Menu.Items className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg py-4 focus:outline-none z-50">
              {/* User Profile Section */}
              <div className="px-4 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-12 w-12 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-medium">
                    {userInitials}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{userName}</h3>
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-gray-600">
                      <span className="mr-1">Tier {userTier}</span>
                      <img 
                        src={tierImages[userTier]} 
                        alt={`Tier ${userTier} icon`}
                        className="h-4 w-auto" 
                      />
                    </div>
                  </div>
                </div>
                <button className="w-full mt-2 py-2 px-4 text-center text-green-600 rounded-lg border border-green-600" onClick={handleSettings}>
                  Account settings
                </button>
              </div>

              <div className="border-t border-gray-100" />

              {/* Menu Items */}
              <div className="py-2">
                <Menu.Item>
                  {({ active }) => (
                    <a href="/voucher-management" className={`${active ? 'bg-gray-50' : ''} flex items-center px-4 py-2 text-gray-700`}>
                      <Gift className="h-5 w-5 mr-3" />
                      <span>My Vouchers</span>
                    </a>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <a href="/recipient-management" className={`${active ? 'bg-gray-50' : ''} flex items-center px-4 py-2 text-gray-700`}>
                      <Heart className="h-5 w-5 mr-3" />
                      <span>Recipients</span>
                    </a>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <a href="https://docs.google.com/forms/d/e/1FAIpQLSfV3yVqbi-mTCriuNTjCQRGh4Qt_hYgDdj5kAXGxbABN0s-xQ/viewform" className={`${active ? 'bg-gray-50' : ''} flex items-center px-4 py-2 text-gray-700`}>
                      <Wallet className="h-5 w-5 mr-3" />
                      <span>Wallet</span>
                    </a>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <a href="/marketplace" className={`${active ? 'bg-gray-50' : ''} flex items-center px-4 py-2 text-gray-700`}>
                      <Star className="h-5 w-5 mr-3" />
                      <span>Marketplace</span>
                    </a>
                  )}
                </Menu.Item>

                <div className="border-t border-gray-100 my-2" />

                <Menu.Item>
                  {({ active }) => (
                    <a onClick={handleSignOut} className={`${active ? 'bg-gray-50' : ''} flex items-center px-4 py-2 text-red-600 cursor-pointer`}>
                      <LogOut className="h-5 w-5 mr-3" />
                      <span>Sign Out</span>
                    </a>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Menu>
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="mt-4 md:hidden">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="What are you looking for?"
            className="w-full px-4 py-2 rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500" />
          <button className="absolute right-2 top-1/2 -translate-y-1/2">
            <Search className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;